import { useCallback, useEffect, useState } from 'react';
import { defer, Observable } from 'rxjs';
import { makeDebug } from '../../utils/makeDebug';
import { shareReplay } from '../../utils/rxExtensions';
import { retry } from '../../utils/utils';
import { isDebugEnabled } from '../debugging';
import { auth, User } from '../firebase/firebaseAccess';

const debug = makeDebug('user:auth', isDebugEnabled('userAuth'));

export const authUserState$: Observable<User | null> = defer(
  () =>
    new Observable<User | null>(function (subscriber) {
      const unsubscribe = auth.onIdTokenChanged({
        next: v => {
          subscriber.next(v);
        },
        error: e => subscriber.error(e),
        complete: () => subscriber.complete(),
      });
      return { unsubscribe: unsubscribe };
    }),
).pipe(shareReplay(1));

export const useAuthToken = () => {
  const [token, setToken] = useState<string>();

  useEffect(() => {
    const sub = authUserState$.subscribe(user => {
      debug('current user auth state changed: %O', user);
      if (!user) {
        setToken(undefined);
        return;
      }
      retry(attempt => user.getIdToken(attempt > 2), 5)
        .then(token => {
          setToken(token);
        })
        .catch(e => {
          console.error("error get current user's auth token: ", e);
        });
    });

    return () => sub.unsubscribe();
  }, []);

  const refreshToken = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    const res = await retry(
      () => user.getIdToken(true),
      5,
      () => 1000,
    );
    setToken(res);
    return res;
  }, []);

  return [token, refreshToken] as const;
};
